import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { colors, glyphs, useMediaQuery } from "../../../Styles";
import { NewSelect } from "../..";
import { field, FilterContent, NewFieldStyles } from "../../../Definitions";
import { getLabelFromKey, ValueType } from "../../../Definitions/valueTypes";
import { createDisplayTypesFromOptions } from "../../../Definitions/filterTypes";
import { SelectOptions, useFeedcurveOptions, useGroupOptions, useMilestoneOptions, useSectionOptions, useSystemOptions } from "../../_Components";
import { ReportModalTypes, useReportContext } from "../_Reports/reportModalContext";

export default function ReportModalLvl2() {
  const { t } = useTranslation()
  const { state: { report,  template, currentViewType }, dispatch } = useReportContext();
  const mobileXs = useMediaQuery(`(max-width: 793px)`);
  const [selectFields, setSelectFields] = React.useState<field[]>([{ id: -1 }]);
  const _reportModal = currentViewType === ReportModalTypes.createReport
  const _templateModal = currentViewType === ReportModalTypes.createTemplate || currentViewType === ReportModalTypes.copyTemplate
  
  // #region | Select Parameters
  const { feedCurveOptions } = useFeedcurveOptions();
  const { allSectionOptions: sectionOptions } = useSectionOptions();
  const groupOptions = useGroupOptions();
  const milestoneOptions = useMilestoneOptions();
  const systemOptions = useSystemOptions();

  let options: Partial<SelectOptions> = {};

  if (report.dataLevel === 1 || template.dataLevel === 1) {
    options = {
      groupOptions,
      feedcurveOptions: feedCurveOptions,
      milestoneOptions,
      systemOptions,
      sectionOptions
    };
  }
  
  if (report.dataLevel === 2 || template.dataLevel === 2) {
    options = {
      groupOptions
    };
  }
  
  if (report.dataLevel === 3 || template.dataLevel === 3) {
    options = {
      systemOptions
    };
  }
  const source = _reportModal ? report : _templateModal ? template : null;
  
  const selectParameters = createDisplayTypesFromOptions(options, (report.dataLevel === 1 || template.dataLevel === 1));

  const filterParamOptions = selectParameters.map((type) => ({
    label: t(`params.${type.label}`, {defaultValue: type.label}), 
    id: type.id
  }));
  // #endregion

  // If reportData already have values to show
  useEffect(() => {
    if (source?.filterParameter && source.filterParameter.length && source.filterParameter[0].length) {
      const updatedFields: field[] = source.filterParameter[0].map((param) => {
        const label = getLabelFromKey(param.key);
        const id = selectParameters.find((p) => p.label === label)?.id ?? -1;
        return { id, value: param.value };
      });
      updatedFields.push({ id: -1 });
      setSelectFields(updatedFields);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.filterParameter, template?.filterParameter]);
  
  // Handle filter type change
  const handleFilterTypeChange = (index: number, id: number) => {
    const updatedSelectFields = [...selectFields]
    ;
    if (updatedSelectFields[index].id === -1) {
      updatedSelectFields.push({ id: -1 });
    }
    updatedSelectFields[index] = { id, value: undefined };

    const selectedParameter = selectParameters.find((param) => param.id === id);
    if (selectedParameter) {
      const updatedFilterParameter = source?.filterParameter?.[0] ? [...source.filterParameter[0]] : [];
      updatedFilterParameter[index] = {
        key: selectedParameter.key,
        value: []
      };
      _reportModal ? dispatch({report: {filterParameter: [updatedFilterParameter]}}) : dispatch({template: {filterParameter: [updatedFilterParameter]}});
    }
    setSelectFields(updatedSelectFields);
  };

  // Handle filter value change
  const handleFilterValueChange = (index: number, values: ValueType[]) => {
    const updatedFilterParameter = Array.isArray(source?.filterParameter?.[0]) ? [...source.filterParameter[0]] : [];
  
    if (!updatedFilterParameter[index]) return;
  
    updatedFilterParameter[index].value = values;
    _reportModal ? dispatch({report: {filterParameter: [updatedFilterParameter]}}) : dispatch({template: {filterParameter: [updatedFilterParameter]}});
  };

  // Remove a SelectField by index
  const handleRemoveField = useCallback((index: number) => {
    const updatedSelectFields = [...selectFields];
    updatedSelectFields.splice(index, 1);
    setSelectFields(updatedSelectFields);
  
    const updatedFilterParameter= source?.filterParameter?.[0];
    updatedFilterParameter?.splice(index, 1);
    _reportModal ? dispatch({report: {filterParameter: [updatedFilterParameter ?? []]}}) : dispatch({template: {filterParameter: [updatedFilterParameter ?? []]}});

  }, [_reportModal, dispatch, selectFields, source?.filterParameter]);

  // #region | Rendered Fields
  const renderedFields = selectFields.map((field, index) => {
    // Remove already used options from optionslist
    const selectedIds = selectFields
    .filter((f, i) => i !== index && f.id !== undefined)
    .map((f) => f.id); 
    const availableOptions = filterParamOptions.filter((option) => !selectedIds.includes(option.id));

    return (
      <div key={field.id !== undefined ? `${field.id}-${index}` : index} style={{ 
        width: "100%", 
        display: "flex", 
        flexDirection: "column", 
        gap: "10px", 
        marginBottom: index === selectFields.length - 1 ? "0px" : "30px"
      }}>
        <div style={{ display: "flex", flexDirection: "row", gap: "20px", alignItems: "end", justifyContent: "space-between" }}>
          <NewSelect
            placeholder={t("select", {defaultValue: "select"})}
            key={index+String(field.id)}
            options={availableOptions}
            onChange={(option) => {
              option?.id && handleFilterTypeChange(index,  Number(option.id));
            }}
            fieldOverrides={{
              fieldWidth: mobileXs ? "250px" : "280px",
              fieldStyle: NewFieldStyles.blueDarkSecondary
            }}
            valueOverrides={{
              defaultValue: filterParamOptions.find((parameter) => {return parameter.id === field.id})
            }}
          />
          {field.id !== -1 && ( <>
            <div style={{cursor: "pointer"}} onClick={() => handleRemoveField(index)}>
              <glyphs.Delete size="25px"/>
            </div>
          </> )}
        </div>
        {/* Content based on selected filter parameter */}
        {field.id !== -1 && (
          <div style={{    
            width: "100%",
            height: "fit-content",
            boxSizing: "border-box",
            backgroundColor: colors.greyLightSecondary,
            padding: "10px",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <FilterContent 
              filter={field} 
              index={index} 
              filterParameterTypes={selectParameters} 
              handleFilterValueChange={(values) => {handleFilterValueChange(index, values)}}
            />
          </div>
        )}
      </div>
    )
  })
  // #endregion

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "start",
      flexDirection: "column",
      boxSizing: "border-box"
    }}>
      {renderedFields}
    </div>
  )
}